import { NgModule, ErrorHandler } from '@angular/core';
import { RouterModule } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PerfectScrollbarModule, PERFECT_SCROLLBAR_CONFIG, PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';

import { rootRouterConfig } from './app.routing';
import { SharedModule } from './shared/shared.module';
import { AppComponent } from './app.component';

import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ErrorHandlerService } from './shared/services/error-handler.service';
import { TokenInterceptor } from './auth/interceptors/token.interceptor';
import { environment } from '../environments/environment';
import { AuthModule } from './auth/auth.module';
import { FeaturesModule } from './features/features.module';
import { ApiModule } from './api/api.module';
import { NgxChartsModule } from '@swimlane/ngx-charts';

import { NgxsModule } from '@ngxs/store';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';

import { SifarniciState } from './root-store/sifarnici/codebook.state';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { NgxLoadingModule, ngxLoadingAnimationTypes } from 'ngx-loading';
import { LoadingInterceptor } from './shared/services/loading.interceptor';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorIntlSrb } from './shared/components/paginator/paginatorClass';
import { ErrorInterceptor } from './shared/services/error.interceptor';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { RgzService } from './services/rgz.service';
import { AuthState } from './root-store/auth/auth.state';
import { OAuthModuleConfig } from 'angular-oauth2-oidc';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
// AoT requires an exported function for factories
export function HttpLoaderFactory(httpClient: HttpClient) {
    return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
    suppressScrollX: true
};

export function authConfigFactory(): OAuthModuleConfig {
    return {
        resourceServer: {
            allowedUrls: [environment.usersApiUrl.toLowerCase()],
            sendAccessToken: true,
        }
    };
}

//I keep the new line
@NgModule({
    imports: [
        BrowserModule,
        NgxChartsModule,
        BrowserAnimationsModule,
        SharedModule,
        FeaturesModule,
        AuthModule,
        HttpClientModule,
        PerfectScrollbarModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        NgxLoadingModule.forRoot({
            animationType: ngxLoadingAnimationTypes.circle,
            backdropBackgroundColour: 'rbga(0,0,0.1)',
            backdropBorderRadius: '4px',
            primaryColour: '#10174C',
            secondaryColour: '#ccc',
            tertiaryColour: '#000000',
            fullScreenBackdrop: true
        }),
        NgxsModule.forRoot([
            AuthState,
            SifarniciState
        ], { developmentMode: !environment.production }),
        NgxsRouterPluginModule.forRoot(),
        NgxsReduxDevtoolsPluginModule.forRoot(),
        NgxsStoragePluginModule.forRoot({ key: ['auth'] }),
        RouterModule.forRoot(rootRouterConfig, { useHash: false }),
        !environment.production ? StoreDevtoolsModule.instrument() : [],
        ApiModule,
    ],
    declarations: [
        AppComponent,
    ],
    providers: [
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
        { provide: PERFECT_SCROLLBAR_CONFIG, useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG },
        { provide: MAT_DATE_LOCALE, useValue: 'sr-Latn' },
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true, },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: MatPaginatorIntl, useClass: MatPaginatorIntlSrb },
        { provide: OAuthModuleConfig, useFactory: authConfigFactory },
        { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { disableTooltipInteractivity: true } },
        RgzService
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
